import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { apiClient } from '../../api/client';

interface EmailVerificationProps {
  email: string;
  onVerificationComplete?: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ email, onVerificationComplete }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState(['', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info' | null; text: string }>({
    type: null,
    text: ''
  });
  const [countdown, setCountdown] = useState(0);

  // Refs for input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (newCode.every(digit => digit !== '') && value) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);

    if (pastedData.length === 5) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[4]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    setLoading(true);
    setMessage({ type: null, text: '' });

    try {
      const response = await apiClient.post('/api/auth/verify-email', {
        email,
        code: verificationCode
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Email verified successfully! Redirecting to login...'
        });

        // Call callback if provided
        if (onVerificationComplete) {
          onVerificationComplete();
        }

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Email verified! Please log in to continue.' }
          });
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Verification failed'
        });
        setCode(['', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error: any) {
      console.error('Verification failed:', error);

      let errorMessage = 'Verification failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setMessage({
        type: 'error',
        text: errorMessage
      });
      setCode(['', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setMessage({ type: null, text: '' });

    try {
      const response = await apiClient.post('/api/auth/resend-verification', {
        email
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'A new verification code has been sent to your email.'
        });
        setCountdown(60); // 60 second cooldown
        setCode(['', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error: any) {
      console.error('Resend failed:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to resend code. Please try again.'
      });
    } finally {
      setResending(false);
    }
  };

  const handleManualSubmit = () => {
    const verificationCode = code.join('');
    if (verificationCode.length === 5) {
      handleVerify(verificationCode);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-card backdrop-blur-sm border-coral-500/20">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-coral-500/20 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-coral-700 dark:text-foreground-muted" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Verify Your Email
            </CardTitle>
            <p className="text-foreground-secondary mt-2">
              We've sent a 5-digit verification code to:
            </p>
            <p className="text-coral-700 dark:text-foreground-muted font-medium mt-1">
              {email}
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Status Message */}
            {message.type && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-300'
                  : message.type === 'error'
                  ? 'bg-red-500/10 border border-red-500/20 text-red-300'
                  : 'bg-blue-500/10 border border-blue-500/20 text-blue-300'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-sm">{message.text}</p>
              </div>
            )}

            {/* Code Input */}
            <div>
              <label className="block text-sm font-medium text-coral-700 dark:text-foreground-muted mb-3 text-center">
                Enter Verification Code
              </label>
              <div className="flex gap-3 justify-center">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={loading}
                    className="w-14 h-14 text-center text-2xl font-bold bg-input border-2 border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-coral-500/40 focus:border-coral-500/40 transition-colors disabled:opacity-50"
                  />
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleManualSubmit}
              disabled={loading || code.some(digit => digit === '')}
              className="w-full bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-coral-500/40"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Verifying...
                </div>
              ) : (
                'Verify Email'
              )}
            </button>

            {/* Resend Code */}
            <div className="text-center space-y-2">
              <p className="text-sm text-foreground-muted">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResend}
                disabled={resending || countdown > 0}
                className="inline-flex items-center gap-2 text-coral-700 dark:text-foreground-muted hover:text-coral-800 dark:hover:text-foreground disabled:text-coral-500/50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
              </button>
            </div>

            {/* Additional Info */}
            <div className="text-center text-xs text-foreground-muted pt-4 border-t border-coral-500/10">
              <p>
                The verification code expires in 15 minutes. Check your spam folder if you don't see the email.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerification;
